import type { CalculationSettings } from '../config/calculationSettings';
import {
  bracketsForState,
  calculateStampDuty,
} from '../config/stampDuty';
import type {
  AustralianState,
  LoanBreakdown,
  StampDutyBracket,
  StampDutyResult,
} from '../types';

export interface PropertyInput {
  hasPurchase: boolean;
  purchasePrice: number;
  depositAvailable: number;
  state: AustralianState;
  isForeign: boolean;
  stampDutyBrackets: Record<string, StampDutyBracket[]>;
  settings: CalculationSettings;
}

const ZERO_SD: StampDutyResult = { base: 0, foreign: 0, total: 0 };

export function computeLoan(input: PropertyInput): LoanBreakdown {
  if (!input.hasPurchase) {
    return {
      stampDuty: ZERO_SD,
      effectiveDeposit: 0,
      loanAmount: 0,
      lvr: 0,
      fees: 0,
      totalRequired: 0,
      depositGap: 0,
    };
  }

  const brackets = bracketsForState(input.stampDutyBrackets, input.state);
  const stampDuty = calculateStampDuty(
    input.purchasePrice,
    brackets,
    input.isForeign,
  );
  const effectiveDeposit = Math.max(
    0,
    input.depositAvailable - stampDuty.total,
  );
  const loanAmount = Math.max(0, input.purchasePrice - effectiveDeposit);
  const lvr =
    input.purchasePrice > 0 ? (loanAmount / input.purchasePrice) * 100 : 0;
  const fees = input.purchasePrice * input.settings.purchaseFeesPct;
  const totalRequired = Math.round(
    input.purchasePrice - loanAmount + stampDuty.total + fees,
  );
  const depositGap = Math.round(input.depositAvailable - totalRequired);

  return {
    stampDuty,
    effectiveDeposit,
    loanAmount,
    lvr,
    fees,
    totalRequired,
    depositGap,
  };
}
